/** We have a few different CnadleSticks chart
 * The usecases are too different to combine them all
 * But we still want to be able to use indicator on every one of them
 *
 * That's why we create a Wrapper component that will fetch all needed indicators
 * This wrapper will have one child: a Candlesticks chart,
 * to which we pass the needed info
 *
 * Now I need to take a shit, brb...
 */

import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ComponentDimensions } from '../../../types/Layout';
import { Candle } from '../../../screens/Detail/types';
import {
  IndicatorSingleValue,
  Indicators,
  IndicatorsConfig,
} from '../../../screens/ProfitCalculations/types';
import axios from 'axios';
import { movingAverageColors, movingAverageWaverColors } from '../../../colors';

import MultiChart from '../../MultiChart';
import RSIChart from '../Rsi';
import MACDChart from '../MACD';
import BandCDChart from '../BandCD';
import MarketCipherWavesComponent from '../MarketCipherWaves';
import PriceOverExtensionChart from '../PriceOverExtension';
import ChannelOverExtentionChart from '../ChannelOverExtention';
import { isZoningBand } from '../CandleSticks';
import IndicatorsLegend from '../CandleSticks/IndicatorsLegend';
import {
  getIndicatorConfig,
  setIndicatorConfigStorage,
} from '../../../helpers/IndicatorStorage';
import ServicePaths from '../../../bridge/utils/ServicePaths';
import { Exchange } from '../../../types/exchange';
import { TimeFrame } from '../../../types/Bot';

interface Props extends PropsWithChildren {
  dimensions: ComponentDimensions | undefined;
  useTimeFrameSpecificConfig?: boolean;
  candles: Candle[] | undefined;
  baseAsset: string;
  quoteAsset: string;
  timeFrame: TimeFrame | undefined;
  exchange: Exchange;
  shouldFetch?: boolean;
  useIndicators?: boolean;
  useParentIndicators?: boolean;
  indicatorConfig?: IndicatorsConfig;
}

const IndicatorWrapper = (props: Props) => {
  const { children } = props;

  const {
    dimensions,
    candles,
    baseAsset,
    quoteAsset,
    timeFrame,
    exchange,
    useIndicators = true,
    useTimeFrameSpecificConfig = true,
    useParentIndicators = false,
  } = props;

  const parentIndicatorsRef = useRef();

  const [indicators, setIndicators] = useState<Indicators | undefined>();
  const [indicatorsConfig, setIndicatorsConfig] = useState(
    props.indicatorConfig ??
      getIndicatorConfig(
        baseAsset,
        quoteAsset,
        useTimeFrameSpecificConfig ? timeFrame : undefined,
      ),
  );

  // useEffect(() => {
  //   /** When these values change, the indicator config needs to be updated */
  //   setIndicatorsConfig(
  //     getIndicatorConfig(
  //       baseAsset,
  //       quoteAsset,
  //       useTimeFrameSpecificConfig ? timeFrame : undefined,
  //     ),
  //   );
  // }, [baseAsset, quoteAsset, timeFrame, useTimeFrameSpecificConfig]);

  const handleIndicatorsConfigChange = useCallback(
    (updatedIndicatorConfig: IndicatorsConfig) => {
      setIndicatorsConfig(updatedIndicatorConfig);
      setIndicatorConfigStorage(
        baseAsset,
        quoteAsset,
        useTimeFrameSpecificConfig ? timeFrame : undefined,
        updatedIndicatorConfig,
      );
    },
    [baseAsset, quoteAsset, timeFrame, useTimeFrameSpecificConfig],
  );

  const isIndicatorEnabled = useMemo(
    () =>
      indicatorsConfig.ma.simple.isEnabled ||
      indicatorsConfig.ma.exponential.isEnabled ||
      indicatorsConfig.ma.waver.simple.isEnabled ||
      indicatorsConfig.ma.waver.exponential.isEnabled ||
      indicatorsConfig.bands.bb.overlay.isEnabled ||
      indicatorsConfig.bands.keltner.overlay.isEnabled ||
      indicatorsConfig.rsi.standard ||
      indicatorsConfig.rsi.stochastic ||
      indicatorsConfig.rsi.waver.standard ||
      indicatorsConfig.rsi.waver.stochastic ||
      indicatorsConfig.rsi.connors ||
      indicatorsConfig.ma.macd ||
      indicatorsConfig.bands.bb.bbcd.isEnabled ||
      indicatorsConfig.bands.keltner.kccd.isEnabled ||
      indicatorsConfig.marketCipher.waves.isEnabled ||
      indicatorsConfig.ma.priceOverExtension.isEnabled ||
      indicatorsConfig.ma.waver.priceOverExtension.isEnabled,
    [
      indicatorsConfig.ma.exponential.isEnabled,
      indicatorsConfig.ma.simple.isEnabled,
      indicatorsConfig.ma.waver.simple.isEnabled,
      indicatorsConfig.ma.waver.exponential.isEnabled,
      indicatorsConfig.bands.bb.overlay.isEnabled,
      indicatorsConfig.bands.keltner.overlay.isEnabled,
      indicatorsConfig.rsi.standard,
      indicatorsConfig.rsi.stochastic,
      indicatorsConfig.rsi.waver.standard,
      indicatorsConfig.rsi.waver.stochastic,
      indicatorsConfig.rsi.connors,
      indicatorsConfig.ma.macd,
      indicatorsConfig.bands.bb.bbcd.isEnabled,
      indicatorsConfig.bands.keltner.kccd.isEnabled,
      indicatorsConfig.marketCipher.waves.isEnabled,
      indicatorsConfig.ma.priceOverExtension.isEnabled,
      indicatorsConfig.ma.waver.priceOverExtension.isEnabled,
    ],
  );

  /** Fetch the indicators */
  useEffect(() => {
    if (!candles?.length) {
      return;
    }

    /** Stop if nothing is enabled */
    if (!isIndicatorEnabled) {
      return;
    }

    if (!baseAsset || !quoteAsset || !timeFrame) {
      return;
    }

    const fetch = async () => {
      const result = await axios({
        method: 'POST',
        url: ServicePaths.PROFIT_CALCULATIONS.GetIndicators,
        data: {
          candles,
          candleInfo: {
            baseAsset,
            quoteAsset,
            timeFrame,
            exchange,
          },
          indicatorsConfig,
        },
      });

      if (result?.data) {
        setIndicators(result.data);

        if (!parentIndicatorsRef.current) {
          parentIndicatorsRef.current = result.data;
        }
      }
    };

    fetch();
  }, [
    baseAsset,
    candles,
    indicatorsConfig,
    isIndicatorEnabled,
    quoteAsset,
    timeFrame,
    exchange,
  ]);

  const indicatorData = useMemo(() => {
    return useParentIndicators
      ? parentIndicatorsRef.current || indicators
      : indicators;
  }, [indicators, useParentIndicators]);

  const movingAverages = useMemo(() => {
    const movingAverages: Array<{
      data: IndicatorSingleValue[];
      color: string;
      label: string;
    }> = [];

    if (indicatorsConfig.ma.simple.isEnabled && indicatorData?.ma?.simple) {
      movingAverages.push({
        data: indicatorData.ma.simple,
        color: movingAverageColors[0],
        label: `SMA (${indicatorsConfig.ma.simple.period})`,
      });
    }

    if (
      indicatorsConfig.ma.exponential.isEnabled &&
      indicatorData?.ma?.exponential
    ) {
      movingAverages.push({
        data: indicatorData.ma.exponential,
        color: movingAverageColors[1],
        label: `EMA (${indicatorsConfig.ma.exponential.period})`,
      });
    }

    if (
      indicatorsConfig.ma.waver.simple.isEnabled &&
      indicatorData?.ma?.waver?.simple
    ) {
      movingAverages.push(
        ...indicatorData.ma.waver.simple.map((sma, i) => ({
          data: sma,
          color: movingAverageWaverColors[i],
          label: `EMA (${indicatorsConfig.ma.waver.simple.periods[i]})`,
        })),
      );
    } else if (
      indicatorsConfig.ma.waver.exponential.isEnabled &&
      indicatorData?.ma?.waver?.exponential
    ) {
      movingAverages.push(
        ...indicatorData.ma.waver.exponential.map((sma, i) => ({
          data: sma,
          color: movingAverageWaverColors[i],
          label: `EMA (${indicatorsConfig.ma.waver.exponential.periods[i]})`,
        })),
      );
    }

    return movingAverages.length ? movingAverages : undefined;
  }, [
    indicatorData?.ma?.exponential,
    indicatorData?.ma?.simple,
    indicatorData?.ma?.waver?.exponential,
    indicatorData?.ma?.waver?.simple,
    indicatorsConfig.ma.exponential.isEnabled,
    indicatorsConfig.ma.exponential.period,
    indicatorsConfig.ma.simple.isEnabled,
    indicatorsConfig.ma.simple.period,
    indicatorsConfig.ma.waver.exponential.isEnabled,
    indicatorsConfig.ma.waver.exponential.periods,
    indicatorsConfig.ma.waver.simple.isEnabled,
    indicatorsConfig.ma.waver.simple.periods,
  ]);

  const bollingerBand = useMemo(
    () =>
      indicatorData?.bands?.bb?.zoning || indicatorData?.bands?.bb?.standard
        ? {
            data:
              indicatorData?.bands?.bb?.zoning ||
              indicatorData?.bands?.bb?.standard,
            signalPeriod: indicatorsConfig.bands.bb.overlay.period,
          }
        : undefined,
    [
      indicatorData?.bands?.bb?.standard,
      indicatorData?.bands?.bb?.zoning,
      indicatorsConfig.bands.bb.overlay.period,
    ],
  );

  const keltnerChannel = useMemo(
    () =>
      indicatorData?.bands?.keltner?.zoning ||
      indicatorData?.bands?.keltner?.standard
        ? {
            data:
              indicatorData?.bands?.keltner?.zoning ||
              indicatorData?.bands?.keltner?.standard,
            signalPeriod: indicatorsConfig.bands.keltner.overlay.period,
          }
        : undefined,
    [
      indicatorData?.bands?.keltner?.standard,
      indicatorData?.bands?.keltner?.zoning,
      indicatorsConfig.bands.keltner.overlay.period,
    ],
  );

  const relativeStrengthIndex = useMemo(
    () => indicatorData?.rsi?.standard,
    [indicatorData?.rsi?.standard],
  );

  const stochasticRelativeStrengthIndex = useMemo(
    () => indicatorData?.rsi?.stochastic,
    [indicatorData?.rsi?.stochastic],
  );

  const relativeStrengthIndexWaver = useMemo(
    () => indicatorData?.rsi?.waver?.standard,
    [indicatorData?.rsi?.waver?.standard],
  );

  const stochasticRelativeStrengthIndexWaver = useMemo(
    () => indicatorData?.rsi?.waver?.stochastic,
    [indicatorData?.rsi?.waver?.stochastic],
  );

  const connorsRelativeStrengthIndex = useMemo(
    () => indicatorData?.rsi?.connors,
    [indicatorData?.rsi?.connors],
  );

  const movingAverageConvergenceDivergence = useMemo(
    () => indicatorData?.ma?.macd,
    [indicatorData?.ma?.macd],
  );

  const bollingerBandConvergenceDivergence = useMemo(
    () => indicatorData?.bands?.bb?.bbcd,
    [indicatorData?.bands?.bb?.bbcd],
  );

  const keltnerChannelConvergenceDivergence = useMemo(
    () => indicatorData?.bands?.keltner?.kccd,
    [indicatorData?.bands?.keltner?.kccd],
  );

  const marketCipherWaves = useMemo(
    () => indicatorData?.marketCipher?.waves,
    [indicatorData?.marketCipher?.waves],
  );

  const priceOverExtension = useMemo(
    () => indicatorData?.ma?.priceOverExtension,
    [indicatorData?.ma?.priceOverExtension],
  );

  const priceOverExtensionWaver = useMemo(
    () => indicatorData?.ma?.waver?.priceOverExtension,
    [indicatorData?.ma?.waver?.priceOverExtension],
  );

  const bollingerBandOverExtension = useMemo(
    () => indicatorData?.bands?.bb?.overExtension,
    [indicatorData?.bands?.bb?.overExtension],
  );

  const keltnerChannelOverExtension = useMemo(
    () => indicatorData?.bands?.keltner?.overExtension,
    [indicatorData?.bands?.keltner?.overExtension],
  );

  /** The first child should always be the main chart */
  const candlesticksChart = useMemo(() => {
    const isArray = Array.isArray(children);
    const chartComponent = isArray ? children[0] : children;

    const chartComponentWithProps = React.cloneElement(chartComponent, {
      key: 'Main Candlesticks chart',
      movingAverages,
      bollingerBand,
      keltnerChannel,
    });

    return chartComponentWithProps;
  }, [bollingerBand, children, keltnerChannel, movingAverages]);

  // The second child is optional and is meant for chart options at the moment
  const chartOptionsComponent = useMemo(() => {
    const isArray = Array.isArray(children);

    if (!isArray) {
      return undefined;
    }

    const filteredChildren = children.filter((c) => !!c);

    if (filteredChildren.length < 2) {
      return undefined;
    }

    return filteredChildren[1];
  }, [children]);

  return (
    <div className="candlesticks-root">
      <MultiChart dimensions={dimensions}>
        {candlesticksChart}
        {/** START OF INDICATORS */}
        {(priceOverExtension || priceOverExtensionWaver) && (
          <PriceOverExtensionChart
            key={'Price Over Extension'}
            label={'Price Over Extension'}
            data={priceOverExtension}
            waver={priceOverExtensionWaver}
            overExtensionThreshold={
              indicatorsConfig.ma.waver.priceOverExtension
                .overExtensionThreshold ||
              indicatorsConfig.ma.waver.priceOverExtension
                .overExtensionThreshold
            }
          />
        )}
        {bollingerBand?.data && bollingerBandOverExtension && (
          <ChannelOverExtentionChart
            key={'Bollinger Band Over Extension'}
            label={'Bollinger Band Over Extension'}
            data={bollingerBandOverExtension}
            isZoningBand={isZoningBand(bollingerBand.data)}
          />
        )}
        {keltnerChannel?.data && keltnerChannelOverExtension && (
          <ChannelOverExtentionChart
            key={'Keltner Channel Over Extension'}
            label={'Keltner Channel Over Extension'}
            data={keltnerChannelOverExtension}
            isZoningBand={isZoningBand(keltnerChannel.data)}
          />
        )}
        {(relativeStrengthIndex || relativeStrengthIndexWaver) && (
          <RSIChart
            key={'Relative Strength Index'}
            label={'Relative Strength Index'}
            rsi={relativeStrengthIndex}
            waver={relativeStrengthIndexWaver}
            overExtensionThreshold={
              indicatorsConfig.rsi.waver.standard.isEnabled
                ? indicatorsConfig.rsi.waver.standard.overExtensionThreshold
                : indicatorsConfig.rsi.standard.overExtensionThreshold
            }
          />
        )}
        {connorsRelativeStrengthIndex && (
          <RSIChart
            key={'Connors Relative Strength Index'}
            label={'Connors Relative Strength Index'}
            rsi={connorsRelativeStrengthIndex}
            overExtensionThreshold={
              indicatorsConfig.rsi.connors.overExtensionThreshold
            }
          />
        )}
        {(stochasticRelativeStrengthIndex ||
          stochasticRelativeStrengthIndexWaver) && (
          <RSIChart
            key={'Stochastic Relative Strength Index'}
            label={'Stochastic Relative Strength Index'}
            rsi={stochasticRelativeStrengthIndex}
            waver={stochasticRelativeStrengthIndexWaver}
            overExtensionThreshold={
              indicatorsConfig.rsi.waver.stochastic.overExtensionThreshold ||
              indicatorsConfig.rsi.stochastic.overExtensionThreshold
            }
          />
        )}
        {marketCipherWaves && (
          <MarketCipherWavesComponent
            key={'Market Cipher Waves'}
            waves={marketCipherWaves}
          />
        )}
        {movingAverageConvergenceDivergence && (
          <MACDChart
            key={'Moving Average Convergence Divergence'}
            macdInfo={movingAverageConvergenceDivergence}
            showWeightedHistogram={
              indicatorsConfig.ma.macd.showWeightedHistogram
            }
          />
        )}
        {bollingerBandConvergenceDivergence && (
          <BandCDChart
            key={'Bollinger Band Convergence Divergence'}
            label={'Bollinger Band Convergence Divergence'}
            bandcdInfo={bollingerBandConvergenceDivergence}
            showWeightedHistogram={
              indicatorsConfig.bands.bb.bbcd.showWeightedHistogram
            }
          />
        )}
        {keltnerChannelConvergenceDivergence && (
          <BandCDChart
            key={'Keltner Channel Convergence Divergence'}
            label={'Keltner Channel Convergence Divergence'}
            bandcdInfo={keltnerChannelConvergenceDivergence}
            showWeightedHistogram={
              indicatorsConfig.bands.keltner.kccd.showWeightedHistogram
            }
          />
        )}
      </MultiChart>
      {chartOptionsComponent}
      {useIndicators && (
        <IndicatorsLegend
          indicatorsConfig={indicatorsConfig}
          handleIndicatorsConfigChange={handleIndicatorsConfigChange}
          availableWidth={dimensions?.width}
        />
      )}
    </div>
  );
};

export default IndicatorWrapper;
